import { Directive, forwardRef, Attribute, Input } from '@angular/core';
import { Validator, AbstractControl, NG_VALIDATORS } from '@angular/forms';

@Directive({
    selector: '[positiveNumber][formControlName],' +
    '[positiveNumber][formControl],' +
    '[positiveNumber][ngModel]',
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PositiveNumberValidator),
            multi: true
        }
    ]
})
export class PositiveNumberValidator implements Validator {
    constructor( @Attribute('positiveNumber') public positiveNumber: string) {}

    validate(cntrl: AbstractControl): { [key: string]: any } {
        /** 'cntrl' refers Current FormControl element.
         * 'cntrl.root' would be the parent FormGroup element
         *  useful when we need to access other elements in the form
         */
        let val = cntrl.value;
        let returnValue = null;
        if (isNaN(val) || val < 1 || val % 1 !== 0) {
          returnValue = {
            positiveNumber: false
          };
        }
        return returnValue;
    }
}

/**
 * Validator for the maximum allowed value of a positive number
 * */
@Directive({
    selector: '[maxLimit][formControlName],' +
    '[maxLimit][formControl],' +
    '[maxLimit][ngModel]',
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => MaxLimitValidator),
            multi: true
        }
    ]
})
export class MaxLimitValidator implements Validator {
    @Input('maxLimit') maxNumber;
    constructor( @Attribute('maxLimit') public maxLimit: string) {}

    validate(cntrl: AbstractControl): { [key: string]: any } {
        let val = cntrl.value;
        let returnValue = null;
        if (isNaN(val) || val > Number(this.maxNumber)) {
          returnValue = {
            maxLimit: false
          };
        }
        return returnValue;
    }
}


/**
 * Validator for the minimum allowed value of a positive number
 * */
@Directive({
    selector: '[minValue][formControlName],' +
    '[minValue][formControl],' +
    '[minValue][ngModel]',
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => MinValueValidator),
            multi: true
        }
    ]
})
export class MinValueValidator implements Validator {
    @Input('minValue') minNumber;
    constructor( @Attribute('minValue') public minValue: string) {}

    validate(cntrl: AbstractControl): { [key: string]: any } {
        let val = cntrl.value;
        let returnValue = null;
        if (isNaN(val) || val < Number(this.minNumber)) {
          returnValue = {
            minValue: false
          };
        }
        return returnValue;
    }
}


/**
* Directive for validating character limit, without out stopping the user from entering more characters
('maxLength' limits prevents the user from entering more values and doesn't throw an error)
*/
@Directive({
    selector: '[charLimit][formControlName],' +
    '[charLimit][formControl],' +
    '[charLimit][ngModel]',
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => CharLimitValidator),
            multi: true
        }
    ]
})
export class CharLimitValidator implements Validator {
    @Input('charLimit') limit;
    constructor( @Attribute('charLimit') public charLimit: string) {}

    validate(cntrl: AbstractControl): { [key: string]: any } {
        let val = cntrl.value;
        let returnValue = null;
        if ( val && val.length && val.length > Number(this.limit) ) {
          returnValue = {
            charLimit: false
          };
        }
        return returnValue;
    }
}

@Directive({
    selector: '[emailList][formControlName],[emailList][formControl],[emailList][ngModel]',
    providers: [
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => EmailListValidator), multi: true }
    ]
})
export class EmailListValidator implements Validator {
    constructor( @Attribute('emailList') public emailList: string) {}

    validate(cntrl: AbstractControl): { [key: string]: any } {
        let returnValue = null;
        if (cntrl.value && this.trimString(cntrl.value) !== '') {
            let emails = cntrl.value.split(',');
            let regex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            let emailString = '';
            for (let i = 0; i < emails.length; i++) {
                emailString = this.trimString(emails[i]);
                if ( emailString === '' || !regex.test(emailString) ) {
                    returnValue = {
                        emailList: false
                    };
                    break;
                }
            }
        }
        return returnValue;
    }

    trimString(stringToTrim: string) {
      return stringToTrim.replace(/^\s+|\s+$/gm, '');
    }
}

// equal-validator

/**
 * usage :
 *
 *    <input type="password" class="form-control" name="password"
 *    [ngModel]="user.password"
 *    required validateEqual="confirmPassword" reverse="true">
 *
 *    <input type="password" class="form-control" name="confirmPassword"
 *    [ngModel]="user.confirmPassword"
 *    required validateEqual="password">
 *
 */

@Directive({
    selector: '[validateEqual][formControlName],' +
    '[validateEqual][formControl],[validateEqual]' +
    '[ngModel]',
    providers: [
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => EqualValidator), multi: true }
    ]
})
export class EqualValidator implements Validator {

    constructor(@Attribute('validateEqual') public validateEqual: string,
    @Attribute('reverse') public reverse: string) {
    }

    private get isReverse() {
        if (!this.reverse) return false;
        return this.reverse === 'true' ? true : false;
    }

    validate(c: AbstractControl): { [key: string]: any } {
        // self value
        let v = c.value;

        // control vlaue
        let e = c.root.get(this.validateEqual);

        // value not equal
        if (e && v !== e.value && !this.isReverse) {
            return {
                validateEqual: false
            };
        }

        // value equal and reverse
        if (e && v === e.value && this.isReverse) {
            delete e.errors['validateEqual'];
            if (!Object.keys(e.errors).length) e.setErrors(null);
        }

        // value not equal and reverse
        if (e && v !== e.value && this.isReverse) {
            e.setErrors({ validateEqual: false });
        }

        return null;
    }
}

@Directive({
    selector: '[prefixLengthNumber][formControlName],' +
    '[prefixLengthNumber][formControl],' +
    '[prefixLengthNumber][ngModel]',
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PrefixLengthNumberValidator),
            multi: true
        }
    ]
})
export class PrefixLengthNumberValidator implements Validator {
    constructor( @Attribute('prefixLengthNumber') public prefixLengthNumber: number) {}

    validate(cntrl: AbstractControl): { [key: number]: any } {
        let val = cntrl.value;
        let returnValue = null;
        if (isNaN(val) || val < 1 || val % 1 !== 0 || val > 32) {
            returnValue = {
                prefixLengthNumber: false
            };
        }
        return returnValue;
    }
}

/**
 * Validator for decimal number.
 * */
@Directive({
    selector: '[restrictDecimal][formControlName],' +
    '[restrictDecimal][formControl],' +
    '[restrictDecimal][ngModel]',
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => restrictDecimalValidator),
            multi: true
        }
    ]
})
export class restrictDecimalValidator implements Validator {
    @Input('restrictDecimal') deciNumber;
    constructor( @Attribute('restrictDecimal') public restrictDecimal: string) {}

    validate(cntrl: AbstractControl): { [key: string]: any } {
        // two decimal places allowed
        let pattern = /^[0-9]+(\.[0-9]{1,2})?$/;
        let val = cntrl.value;
        let returnValue = null;
        if (isNaN(val) || !pattern.test(val)) {
          returnValue = {
            restrictDecimal: false
          };
        }
        return returnValue;
    }
}


// WEBPACK FOOTER //
// ./src/common/directives.ts